/**
 * OIDC configuration for Cognito hosted UI. We direct users straight at the
 * Google Workspace identity provider so the only way in is via Google (which
 * enforces the organisation's 2FA). The User Pool additionally mandates MFA.
 */

import { WebStorageStateStore } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';
import type { RuntimeConfig } from './config.js';

export function buildOidcConfig(config: RuntimeConfig): AuthProviderProps {
  return {
    authority: config.cognitoAuthority,
    client_id: config.cognitoClientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.postLogoutRedirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    // Don't specify identity_provider - let Cognito auto-select the only available IdP
    // extraQueryParams: { identity_provider: config.identityProvider },
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // Clean the auth code/state out of the URL after sign-in.
    onSigninCallback: () => {
      window.history.replaceState({}, document.title, window.location.pathname);
    },
    // Cognito's logout endpoint differs from the OIDC spec; handled in signOut().
    automaticSilentRenew: true,
  };
}

/**
 * Check if the auth error indicates MFA is required.
 * Cognito returns an error with message containing "Multi-factor authentication".
 */
export function isMfaRequiredError(error: Error): boolean {
  const message = error.message.toLowerCase();
  return message.includes('multi-factor authentication') || 
         message.includes('mfa') && message.includes('required');
}

/**
 * Cognito uses a non-standard /logout endpoint, so trigger it explicitly rather
 * than relying on the OIDC end-session endpoint.
 */
export function cognitoSignOutUrl(config: RuntimeConfig): string {
  const url = new URL(`${config.cognitoDomain}/logout`);
  url.searchParams.set('client_id', config.cognitoClientId);
  url.searchParams.set('logout_uri', config.postLogoutRedirectUri);
  return url.toString();
}
